import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { Field } from "../../components/neu/Field";
import { EntryLines } from "./EntryLines";
import { postEntry } from "../../api/accounting";
import { useApiError } from "../../hooks/useApiError";
import { lineTotals } from "./entryTotals";

const EMPTY = [
  { account_code: "", debit: "", credit: "" },
  { account_code: "", debit: "", credit: "" },
];

export const EntryForm = ({ accounts, branches, onCancel, onSaved }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [branchId, setBranchId] = useState(branches[0]?.id || "");
  const [lines, setLines] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const totals = lineTotals(lines);

  const save = async () => {
    setSaving(true);
    try {
      await postEntry({
        date,
        description,
        branch_id: branchId || null,
        lines: lines
          .filter((l) => l.account_code)
          .map((l) => ({ ...l, debit: Number(l.debit || 0), credit: Number(l.credit || 0) })),
      });
      toast.success(t("acct.entryPosted"));
      onSaved();
    } catch (error) {
      toast.error(describe(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <NeuCard className="grid gap-4 p-6" data-testid="entry-form">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label={t("acct.date")} testId="entry-date" type="date" value={date} onChange={setDate} />
        <Field label={t("acct.description")} testId="entry-description" value={description} onChange={setDescription} />
        <div>
          <p className="label-xs mb-2">{t("acct.branch")}</p>
          <select
            data-testid="entry-branch"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            className="w-full rounded-2xl border border-border/60 bg-background px-4 py-3 text-sm shadow-neu-in focus:outline-none"
          >
            <option value="">—</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <EntryLines lines={lines} accounts={accounts} onChange={setLines} />

      <p className="font-mono text-xs" dir="ltr" data-testid="entry-totals">
        D {totals.debit} / C {totals.credit} · {totals.balanced ? t("acct.balanced") : t("acct.unbalanced")}
      </p>

      <div className="flex gap-2">
        <NeuButton
          variant="primary"
          className="flex-1"
          data-testid="entry-save"
          disabled={saving || !totals.balanced}
          onClick={save}
        >
          {t("acct.post")}
        </NeuButton>
        <NeuButton data-testid="entry-cancel" onClick={onCancel}>
          {t("app.cancel")}
        </NeuButton>
      </div>
    </NeuCard>
  );
};
